/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react'
import { Skeleton } from '@mui/material'
import { CounterProps } from './Counter.types'
import {
  CounterWrapper,
  CounterInput,
  CounterButton,
  SideCounter,
  SideCounterWrapper,
  ReadOnlyLabel
} from './Counter.styles'
import {
  roundNumberToNextDecimalStep,
  executeCounterActions,
  isDisabled,
  getMaxValue
} from './Counter.utils'
import useId from '../../hooks/useId'
import { usePrevious } from '../../hooks/usePrevious'

let inputTimer: ReturnType<typeof setTimeout>
const DEBOUNCE_INPUT_TIME = 750
const Counter = ({
  className = '',
  value: initialValue = 0,
  step: defaultStep = 1,
  min = 0,
  max,
  stock,
  color = 'default',
  styles,
  data = {},
  plusButton: { actions: plusActions, ...plusButtonProps } = {},
  minusButton: { actions: minusActions, ...minusButtonProps } = {},
  activeInput,
  input,
  disabledActions,
  disabled = false,
  updateValueOnDisabled = true,
  sideCounter = false,
  modifierName,
  modifierMap = {},
  'data-testid': testid = 'counter',
  readonly = false,
  allowSetMaxvalue = false,
  customStep,
  loading = false,
  adjustQuantity,
  debounceInputTime = DEBOUNCE_INPUT_TIME,
  ...props
}: CounterProps) => {
  const [value, setValue] = useState(initialValue)
  const { getTestId } = useId(testid)
  const step = useMemo(
    () => customStep || defaultStep,
    [defaultStep, customStep]
  )
  const minusDisabled = isDisabled({ value, step, min, stock, validate: 'min' })
  const plusDisabled = isDisabled({ value, step, max, stock, validate: 'max' })
  const minusDisabledClass = minusDisabled || disabled ? 'disabled' : ''
  const plusDisabledClass = plusDisabled || disabled ? 'disabled' : ''
  const modifier =
    modifierMap[modifierName || ''] ||
    modifierMap[modifierName?.toLowerCase() || ''] ||
    1
  const modifierResult = value * modifier
  const displayValue = Number.isInteger(modifierResult)
    ? modifierResult.toString()
    : (Math.round(modifierResult * 100) / 100).toString()
  const actionsArgs = { value, step, data, setValue, defaultStep, customStep }

  const handlePlusClick = (event: any) => {
    event.preventDefault()
    event.stopPropagation()
    const newValue = value + step
    executeCounterActions({
      newValue,
      disabled: plusDisabled,
      actions: plusActions,
      disabledActions,
      ...actionsArgs
    })
  }

  const handleMinusClick = (event: any) => {
    event.preventDefault()
    event.stopPropagation()
    const newValue = value - step
    executeCounterActions({
      newValue,
      disabled: minusDisabled,
      actions: minusActions,
      ...actionsArgs
    })
  }

  const oldValue = usePrevious(initialValue)

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault()
    event.stopPropagation()
    let newValue = Number(event.target.value) / modifier
    const invalidValue = allowSetMaxvalue
      ? false
      : isDisabled({ value: newValue, min, max, stock })
    if (Number.isInteger(step) && !Number.isInteger(newValue)) {
      newValue = Math.floor(newValue)
    } else {
      newValue = roundNumberToNextDecimalStep(newValue)
    }
    setValue(invalidValue ? value : newValue)
    clearTimeout(inputTimer)
    inputTimer = setTimeout(() => {
      const actions = newValue > value ? plusActions : minusActions
      // sets the max value if the quantity does not chage but the internal value it does and the newValue is higher than the max value
      const { resultFixed, resultValue } = getMaxValue({
        allowSetMaxvalue,
        initialValue,
        newValue,
        stock,
        max,
        oldValue
      })
      executeCounterActions({
        newValue: resultValue,
        disabled: invalidValue,
        actions,
        resultFixed,
        adjustQuantity,
        ...actionsArgs
      })
    }, debounceInputTime)
  }

  // when value changes for external reasons
  useEffect(() => {
    if (updateValueOnDisabled || !disabled) setValue(initialValue)
  }, [initialValue, disabled, updateValueOnDisabled])

  const iconButtonStyles: any = {}

  if (sideCounter && value === 0) {
    const counterProps = typeof sideCounter === 'boolean' ? {} : sideCounter
    return (
      <SideCounterWrapper
        className={`Counter ${className}`}
        sx={styles}
        {...props}>
        <SideCounter
          className={`SideCounter ${plusDisabledClass}`}
          icon="MdAdd"
          color={color}
          onClick={handlePlusClick}
          styles={iconButtonStyles}
          {...plusButtonProps}
          {...counterProps}
        />
      </SideCounterWrapper>
    )
  }

  const notAvailableState = () => {
    if (readonly) return <ReadOnlyLabel>{value}</ReadOnlyLabel>
    if (loading)
      return (
        <>
          <Skeleton variant="text" width={40} />
          <Skeleton variant="text" width={30} />
          <Skeleton variant="text" width={40} />
        </>
      )
  }

  return (
    <CounterWrapper
      className={`Counter ${className}`}
      sx={styles}
      data-testid={getTestId()}
      {...props}>
      {readonly || loading ? (
        notAvailableState()
      ) : (
        <>
          <CounterButton
            className={`MinusButton CounterButton ${minusDisabledClass}`}
            icon="MdRemove"
            color={color}
            onClick={handleMinusClick}
            styles={iconButtonStyles}
            data-testid={getTestId('minus')}
            {...minusButtonProps}
          />
          <CounterInput
            className="CounterInput"
            value={displayValue}
            type="number"
            inputProps={{
              min: 0,
              step
            }}
            readOnly={!activeInput || disabled}
            data-testid={getTestId('input')}
            onChange={handleInput}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
            {...input}
          />
          <CounterButton
            className={`PlusButton CounterButton ${plusDisabledClass}`}
            icon="MdAdd"
            color={color}
            onClick={handlePlusClick}
            styles={iconButtonStyles}
            data-testid={getTestId('plus')}
            {...plusButtonProps}
          />
        </>
      )}
    </CounterWrapper>
  )
}

export default Counter
